import { useEffect, useState, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Package, Edit2, Trash2, AlertTriangle, Upload, Tags, Check, X, Layers, LayoutGrid, Rows3, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { VariantEditor } from "../components/VariantEditor";

const EMPTY_FORM = { name: "", category: "", daily_price: 0, total_stock: 0, buffer_days: 0, description: "", image_url: "", size: "", color: "", brand: "", variants: [] };

const fmtIDR = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const UNCAT = "Tanpa Kategori";

function CategoryChips({ chips, active, onChange, testIdPrefix = "cat" }) {
  return (
    <div className="flex gap-2 overflow-x-auto pos-scroll -mx-1 px-1 py-1" data-testid={`${testIdPrefix}-chips`}>
      {chips.map(c => (
        <button
          key={c}
          type="button"
          data-testid={`${testIdPrefix}-chip-${c}`}
          onClick={() => onChange(c)}
          className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors whitespace-nowrap"
          style={active === c
            ? { background: "var(--brand-primary)", color: "#fff", borderColor: "var(--brand-primary)" }
            : { background: "#fff", color: "#404040", borderColor: "#e5e5e5" }}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function ManageCategoriesDialog({ open, onOpenChange, categories, onChanged }) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const add = async () => {
    const n = newName.trim();
    if (!n) return;
    try {
      await api.post("/categories", { name: n });
      toast.success("Kategori ditambahkan");
      setNewName(""); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditingName(c.name); };
  const saveEdit = async () => {
    try {
      await api.put(`/categories/${editingId}`, { name: editingName.trim() });
      toast.success("Kategori diperbarui");
      setEditingId(null); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"? Produk terkait akan dipindahkan ke "Tanpa Kategori".`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Kategori dihapus");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Kelola Kategori</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input data-testid="new-cat-input" placeholder="Nama kategori baru" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} />
            <Button data-testid="new-cat-add" onClick={add} className="text-white" style={{ background: "var(--brand-primary)" }}><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="border border-neutral-200 rounded max-h-72 overflow-y-auto divide-y divide-neutral-100">
            {categories.length === 0 && <div className="text-sm text-neutral-500 py-6 text-center">Belum ada kategori</div>}
            {categories.map(c => (
              <div key={c.id} className="flex items-center gap-2 p-2" data-testid={`cat-row-${c.id}`}>
                {editingId === c.id ? (
                  <>
                    <Input autoFocus value={editingName} onChange={e => setEditingName(e.target.value)} onKeyDown={e => e.key === "Enter" && saveEdit()} className="h-8" data-testid={`cat-edit-${c.id}`} />
                    <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={saveEdit} data-testid={`cat-save-${c.id}`}><Check className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditingId(null)}><X className="w-3 h-3" /></Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 text-sm">{c.name}</div>
                    <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => startEdit(c)} data-testid={`cat-edit-btn-${c.id}`}><Edit2 className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600" onClick={() => del(c)} data-testid={`cat-del-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Inventory() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useCached("products", []);
  const [categories, setCategories] = useCached("categories", []);
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeCat, setActiveCat] = useState("Semua");
  const [form, setForm] = useState(EMPTY_FORM);
  const fileRef = useRef();

  const loadCategories = () => api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
  const load = () => api.get("/products").then(r => setItems(r.data));
  useEffect(() => { load(); loadCategories(); }, []);

  const chipList = useMemo(() => {
    const set = new Set(categories.map(c => c.name));
    items.forEach(p => { if (p.category) set.add(p.category); });
    const arr = Array.from(set).sort();
    const hasUncat = items.some(p => !p.category);
    return ["Semua", ...arr, ...(hasUncat ? [UNCAT] : [])];
  }, [categories, items]);

  const filtered = useMemo(() => {
    if (activeCat === "Semua") return items;
    if (activeCat === UNCAT) return items.filter(p => !p.category);
    return items.filter(p => p.category === activeCat);
  }, [items, activeCat]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, size: p.size || "", color: p.color || "", brand: p.brand || "", buffer_days: p.buffer_days || 0,
      variants: (p.variants || []).map(v => ({ id: v.id, name: v.name, stock: v.stock, image_url: v.image_url || "", daily_price: v.daily_price ?? "" })) });
    setOpen(true);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Ukuran maks 2MB");
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, image_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const variants = (form.variants || []).filter(v => v.name.trim()).map(v => ({
        id: v.id || "", name: v.name.trim(), stock: Number(v.stock) || 0, image_url: v.image_url || "",
        daily_price: v.daily_price === "" || v.daily_price === null ? null : Number(v.daily_price),
      }));
      const { available, pending_locked, created_at, ...rest } = form;
      const payload = { ...rest, category: form.category.trim(), daily_price: Number(form.daily_price), total_stock: Number(form.total_stock) || 0, buffer_days: Number(form.buffer_days) || 0, variants };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      // Auto-create category if new
      if (payload.category && !categories.some(c => c.name === payload.category)) {
        try { await api.post("/categories", { name: payload.category }); } catch { /* ignore duplicate */ }
      }
      toast.success("Tersimpan");
      setOpen(false); load(); loadCategories();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal simpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Pindahkan alat ini ke Sampah? Bisa dipulihkan dalam 30 hari.")) return;
    await api.delete(`/products/${id}`);
    toast.success("Dipindahkan ke Sampah"); load();
  };

  const canReorder = isOwner;
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("inv_view") || "grid");
  const [gridCols, setGridCols] = useState(() => Number(localStorage.getItem("inv_cols")) || 3);
  useEffect(() => { localStorage.setItem("inv_view", viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem("inv_cols", String(gridCols)); }, [gridCols]);
  const COLS_CLASS = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5" };

  const moveTo = async (pid, newPos) => {
    const cur = items.findIndex(p => p.id === pid);
    if (cur < 0) return;
    const pos = Math.max(1, Math.min(items.length, Number(newPos) || 1)) - 1;
    if (pos === cur) return;
    const arr = [...items];
    const [m] = arr.splice(cur, 1);
    arr.splice(pos, 0, m);
    setItems(arr);
    try { await api.put("/products/reorder", { ids: arr.map(p => p.id) }); toast.success("Urutan diperbarui"); }
    catch { toast.error("Gagal simpan urutan"); load(); }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Inventaris</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Alat Outdoor</h1>
        </div>
        {isOwner && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="manage-categories-btn" variant="outline" onClick={() => setCatOpen(true)}>
              <Tags className="w-4 h-4 mr-1" /> Kelola Kategori
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-product-btn" onClick={openNew} className="text-white" style={{ background: "var(--brand-primary)" }}>
                  <Plus className="w-4 h-4 mr-1" /> Tambah Alat
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editing ? "Edit Alat" : "Tambah Alat"}</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-3">
                  <div><Label>Nama Alat</Label><Input data-testid="prod-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
                  <div>
                    <Label>Kategori</Label>
                    <Input data-testid="prod-category" list="prod-cat-list" value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="Pilih atau ketik baru" />
                    <datalist id="prod-cat-list">
                      {categories.map(c => <option key={c.id} value={c.name} />)}
                    </datalist>
                    <p className="text-[10px] text-neutral-500 mt-1">Kategori baru otomatis tersimpan setelah alat disimpan.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Harga / Hari</Label><Input data-testid="prod-price" type="number" value={form.daily_price} onChange={e => setForm({...form, daily_price: e.target.value})} required /></div>
                    <div><Label>Total Stok {form.variants?.length > 0 && <span className="text-[10px] text-neutral-400">(otomatis dari varian)</span>}</Label>
                      <Input data-testid="prod-stock" type="number" value={form.variants?.length > 0 ? form.variants.reduce((a, v) => a + (Number(v.stock) || 0), 0) : form.total_stock} onChange={e => setForm({...form, total_stock: e.target.value})} disabled={form.variants?.length > 0} required /></div>
                  </div>
                  <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
                  <div><Label>Hari Siap Kembali <span className="text-[10px] text-neutral-400">(masa perawatan/cuci setelah sewa, mis. 1)</span></Label>
                    <Input data-testid="prod-buffer-days" type="number" min="0" value={form.buffer_days} onChange={e => setForm({...form, buffer_days: e.target.value})} placeholder="0" /></div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Ukuran</Label><Input data-testid="prod-size" value={form.size} onChange={e => setForm({...form, size: e.target.value})} placeholder="L / 40L / 4P" /></div>
                    <div><Label className="text-xs">Warna</Label><Input data-testid="prod-color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} placeholder="Hitam" /></div>
                    <div><Label className="text-xs">Merek</Label><Input data-testid="prod-brand" value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} placeholder="Eiger" /></div>
                  </div>
                  <div>
                    <Label>Gambar Alat (dari galeri/kamera)</Label>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="prod-image-input" />
                    <div className="flex items-center gap-3 mt-1">
                      <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1" /> Pilih dari Galeri</Button>
                      {form.image_url && <img src={form.image_url} alt="preview" className="h-16 w-16 rounded object-cover border border-neutral-200" />}
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">Atau paste URL: <Input value={typeof form.image_url === "string" && form.image_url.startsWith("http") ? form.image_url : ""} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://..." className="mt-1" /></p>
                  </div>
                  <VariantEditor variants={form.variants || []} onChange={v => setForm({...form, variants: v})} />
                  <Button type="submit" data-testid="prod-save" className="w-full text-white" style={{background: "var(--brand-primary)"}}>Simpan</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <CategoryChips chips={chipList} active={activeCat} onChange={setActiveCat} testIdPrefix="inv-cat" />

      <div className="flex items-center justify-between flex-wrap gap-3 -mt-1">
        {canReorder
          ? <p className="text-xs text-neutral-500" data-testid="reorder-hint">Atur posisi produk lewat kotak <b>nomor urut</b> di tiap kartu (1 = paling atas). Alat terbaru otomatis di atas.</p>
          : <span />}
        <div className="flex items-center gap-2" data-testid="inv-view-controls">
          <div className="flex border border-neutral-200 rounded-md overflow-hidden">
            <button type="button" onClick={() => setViewMode("grid")} className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${viewMode === "grid" ? "text-white" : "text-neutral-600"}`} style={{background: viewMode === "grid" ? "var(--brand-primary)" : "white"}} data-testid="view-grid"><LayoutGrid className="w-3.5 h-3.5" /> Grid</button>
            <button type="button" onClick={() => setViewMode("list")} className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${viewMode === "list" ? "text-white" : "text-neutral-600"}`} style={{background: viewMode === "list" ? "var(--brand-primary)" : "white"}} data-testid="view-list"><Rows3 className="w-3.5 h-3.5" /> List</button>
          </div>
          {viewMode === "grid" && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-neutral-500">Kolom</span>
              {[2, 3, 4, 5].map(n => (
                <button key={n} type="button" onClick={() => setGridCols(n)} className={`w-7 h-7 rounded text-xs font-semibold border ${gridCols === n ? "text-white border-transparent" : "text-neutral-600 border-neutral-200"}`} style={{background: gridCols === n ? "var(--brand-primary)" : "white"}} data-testid={`cols-${n}`}>{n}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={viewMode === "list" ? "flex flex-col gap-4" : `grid grid-cols-1 sm:grid-cols-2 ${COLS_CLASS[gridCols] || "lg:grid-cols-3"} gap-4`} data-testid="products-grid">
        {filtered.map((p, idx) => {
          const low = p.available <= 1;
          const globalPos = items.findIndex(x => x.id === p.id) + 1;
          const isList = viewMode === "list";
          return (
            <Card
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className={`relative border-neutral-200 shadow-none overflow-hidden ${isList ? "sm:flex sm:items-stretch" : ""}`}
            >
              {canReorder && (
                <div className="absolute z-10 top-2 left-2 flex items-center gap-1 bg-white/95 border border-neutral-200 rounded px-1 py-0.5 shadow-sm">
                  <ArrowUpDown className="w-3 h-3 text-neutral-400" />
                  <input
                    type="number" min={1} max={items.length} defaultValue={globalPos}
                    key={`pos-${p.id}-${globalPos}`}
                    onBlur={e => moveTo(p.id, e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                    className="w-11 h-6 text-xs text-center font-mono-tab border border-neutral-200 rounded"
                    data-testid={`order-input-${p.id}`}
                  />
                </div>
              )}
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className={isList ? "w-full sm:w-44 h-40 sm:h-auto object-cover" : "w-full h-40 object-cover"} />
              ) : (
                <div className={isList ? "w-full sm:w-44 h-40 sm:h-auto bg-neutral-100 flex items-center justify-center" : "w-full h-40 bg-neutral-100 flex items-center justify-center"}>
                  <Package className="w-10 h-10 text-neutral-300" />
                </div>
              )}
              <CardContent className={isList ? "p-5 flex-1" : "p-5"}>
                <div className="flex items-start justify-between">
                  <div className="text-xs uppercase tracking-wider text-neutral-500">{p.category || UNCAT}</div>
                  <div className="flex gap-1 flex-col items-end">
                    {p.pending_locked > 0 && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-200" title="Unit dikunci oleh booking pending" data-testid={`pending-lock-${p.id}`}>
                        {p.pending_locked} pending
                      </span>
                    )}
                    {low && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Stok Kritis
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="font-display font-bold text-lg text-neutral-900 mt-1">{p.name}</h3>
                <div className="flex flex-wrap gap-1 mt-1">
                  {p.brand && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">{p.brand}</span>}
                  {p.size && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">Uk: {p.size}</span>}
                  {p.color && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">{p.color}</span>}
                </div>
                <div className="text-sm text-neutral-600 mt-1 line-clamp-2 min-h-[2.5rem]">{p.description}</div>
                {p.variants?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`variants-${p.id}`}>
                    <span className="text-[10px] uppercase font-bold text-blue-700 flex items-center gap-0.5 mr-1"><Layers className="w-3 h-3" /> Varian:</span>
                    {p.variants.map(v => (
                      <span key={v.id} className={`text-[10px] px-2 py-0.5 rounded border flex items-center gap-1 ${v.available <= 0 ? "bg-red-50 text-red-600 border-red-200" : "bg-neutral-50 text-neutral-700 border-neutral-200"}`}>
                        {v.image_url && <img src={v.image_url} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" />}
                        {v.name} <b>{v.available}/{v.stock}</b>
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase text-neutral-500 tracking-wider">Tersedia</div>
                    <div className="font-mono-tab font-bold text-2xl text-neutral-900">{p.available}<span className="text-neutral-400 text-base font-normal">/{p.total_stock}</span></div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase text-neutral-500 tracking-wider">Harga/Hari</div>
                    <div className="font-mono-tab font-bold" style={{color: "var(--brand-primary)"}}>{fmtIDR(p.daily_price)}</div>
                  </div>
                </div>
                {isOwner && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-neutral-100">
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => del(p.id)} data-testid={`del-${p.id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3 mr-1" /> Hapus</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-neutral-500">Tidak ada alat pada kategori ini</div>}
      </div>

      <ManageCategoriesDialog open={catOpen} onOpenChange={setCatOpen} categories={categories} onChanged={() => { loadCategories(); load(); }} />
    </div>
  );
}
